import { type Plan, parsePlan } from '@projections/engine';
import { type ApiClient, createApiClient } from './apiClient';
import { isApiConfigured, LOCAL_STORAGE_PLANS_KEY } from './config';

/**
 * The plan PERSISTENCE store (PLAN.md M7). One interface, two backends, chosen at construction by
 * whether an API base URL is configured:
 *   - API present  → the Hono RPC client (`hc<AppType>`): plans persist in D1 via the Worker.
 *   - API absent   → localStorage (the OFFLINE fallback): plans persist in the browser.
 *
 * Both deal in the engine `Plan` (validated with the SHARED `PlanSchema`, so a plan the editor saves
 * is byte-identical to one the engine simulates and one the API stores). The returned summaries +
 * records mirror the API's `PlanSummary` / `PlanResponse` contracts so the UI is backend-agnostic.
 */

/** A stored plan's metadata (matches the API `PlanSummary`). */
export interface PlanSummary {
  readonly id: string;
  readonly name: string;
  readonly schemaVersion: number;
  readonly createdAt: string; // ISO-8601
  readonly updatedAt: string;
}

/** A full stored plan record (matches the API `PlanResponse`). */
export interface PlanRecord extends PlanSummary {
  readonly document: Plan;
}

/** Fields supplied when creating/updating a plan. */
export interface PlanWrite {
  readonly name: string;
  readonly document: Plan;
}

export interface PlanStore {
  /** Which backend is active — surfaced in the UI ("offline · localStorage" vs "synced"). */
  readonly mode: 'api' | 'local';
  list(): Promise<PlanSummary[]>;
  get(id: string): Promise<PlanRecord | null>;
  create(write: PlanWrite, id?: string): Promise<PlanRecord>;
  update(id: string, write: PlanWrite): Promise<PlanRecord | null>;
  remove(id: string): Promise<boolean>;
}

/* -------------------------------------------------------------------------- */
/* localStorage backend (offline fallback)                                    */
/* -------------------------------------------------------------------------- */

/** The on-disk shape of one stored plan (document kept as raw JSON the engine re-validates on read). */
interface StoredPlan {
  readonly id: string;
  readonly name: string;
  readonly schemaVersion: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly document: unknown;
}

/** Read the whole `{ id → StoredPlan }` map from localStorage (empty on absent/corrupt). */
function readStoredMap(): Record<string, StoredPlan> {
  if (typeof localStorage === 'undefined') return {};
  const raw = localStorage.getItem(LOCAL_STORAGE_PLANS_KEY);
  if (raw === null) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') return {};
    return parsed as Record<string, StoredPlan>;
  } catch {
    return {};
  }
}

/** Persist the whole `{ id → StoredPlan }` map back to localStorage. */
function writeStoredMap(map: Record<string, StoredPlan>): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(LOCAL_STORAGE_PLANS_KEY, JSON.stringify(map));
}

/** Project a stored record to a list summary (no document). */
function toSummary(stored: StoredPlan): PlanSummary {
  return {
    id: stored.id,
    name: stored.name,
    schemaVersion: stored.schemaVersion,
    createdAt: stored.createdAt,
    updatedAt: stored.updatedAt,
  };
}

/** Hydrate a stored record to a full record, re-validating the document with the shared schema. */
function toRecord(stored: StoredPlan): PlanRecord {
  return { ...toSummary(stored), document: parsePlan(stored.document) };
}

/** Mint a stable id (uuid where available, else a timestamp-random fallback for old runtimes). */
function mintId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `plan-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * localStorage-backed store (the OFFLINE fallback). Persists a `Record<id, StoredPlan>` under
 * `LOCAL_STORAGE_PLANS_KEY`; each document is re-validated with `parsePlan` on the way out, so a
 * hand-edited or stale localStorage value can never poison the app. Internally synchronous, but
 * every method returns a Promise so the UI is identical across both backends.
 */
export function createLocalPlanStore(): PlanStore {
  return {
    mode: 'local',
    list: async () => {
      const map = readStoredMap();
      return Object.values(map)
        .map(toSummary)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)); // newest first
    },
    get: async (id) => {
      const stored = readStoredMap()[id];
      if (stored === undefined) return null;
      try {
        return toRecord(stored);
      } catch {
        // A corrupt/old document can't be hydrated — treat it as absent rather than crashing.
        return null;
      }
    },
    create: async (write, id) => {
      const map = readStoredMap();
      const newId = id ?? mintId();
      if (map[newId] !== undefined) {
        throw new Error(`plan with id "${newId}" already exists`);
      }
      // Validate before persisting so a bad plan never lands in storage.
      const document = parsePlan(write.document);
      const now = new Date().toISOString();
      const stored: StoredPlan = {
        id: newId,
        name: write.name,
        schemaVersion: document.version,
        createdAt: now,
        updatedAt: now,
        document,
      };
      map[newId] = stored;
      writeStoredMap(map);
      return toRecord(stored);
    },
    update: async (id, write) => {
      const map = readStoredMap();
      const existing = map[id];
      if (existing === undefined) return null;
      const document = parsePlan(write.document);
      const stored: StoredPlan = {
        ...existing,
        name: write.name,
        schemaVersion: document.version,
        updatedAt: new Date().toISOString(),
        document,
      };
      map[id] = stored;
      writeStoredMap(map);
      return toRecord(stored);
    },
    remove: async (id) => {
      const map = readStoredMap();
      if (map[id] === undefined) return false;
      delete map[id];
      writeStoredMap(map);
      return true;
    },
  };
}

/* -------------------------------------------------------------------------- */
/* API (Hono RPC) backend                                                     */
/* -------------------------------------------------------------------------- */

/** A minimal structural view of a fetch/RPC response — covers both `Response` and Hono `ClientResponse`. */
interface ResponseLike {
  readonly ok: boolean;
  readonly status: number;
  readonly statusText: string;
  json(): Promise<unknown>;
}

/** Pull the API's `{ error }` envelope off a non-2xx response, falling back to the status text. */
async function apiError(res: ResponseLike): Promise<Error> {
  try {
    const body = (await res.json()) as { error?: unknown };
    if (body !== null && typeof body === 'object' && typeof body.error === 'string') {
      return new Error(body.error);
    }
  } catch {
    // non-JSON body — fall through to the status line
  }
  return new Error(`API request failed (${res.status} ${res.statusText})`);
}

/**
 * RPC-backed store over `hc<AppType>` — maps each method onto the API routes
 * (`client.plans.$get/$post`, `client.plans[':id'].$get/$put/$delete`) and re-validates the
 * returned plan document with `parsePlan`. Wire-timestamps are ISO strings already (the API
 * serialises them), so they pass straight through. On a non-2xx the API's `{ error }` envelope is
 * surfaced as a thrown Error (so the top-bar Save status shows the real reason). The `client` is
 * guaranteed non-null here (the factory only builds this when the API is configured).
 *
 * NOTE on request bodies: the M6 API parses bodies manually (`c.req.json()` + a `safeParse`) rather
 * than via Hono's `zValidator`, so the RPC client does NOT infer a typed `json` body for `$post` /
 * `$put`. We therefore send the (schema-shaped) body through the `init` option — still hitting the
 * same routes and the same shared `createPlanRequestSchema` / `updatePlanRequestSchema` server-side.
 */
export function createApiPlanStore(client: ApiClient): PlanStore {
  /** Map a wire `PlanResponse` to a `PlanRecord`, re-validating the document. */
  const toRecordFromWire = (raw: unknown): PlanRecord => {
    const wire = raw as {
      id: string;
      name: string;
      schemaVersion: number;
      createdAt: string;
      updatedAt: string;
      document: unknown;
    };
    return {
      id: wire.id,
      name: wire.name,
      schemaVersion: wire.schemaVersion,
      createdAt: wire.createdAt,
      updatedAt: wire.updatedAt,
      document: parsePlan(wire.document),
    };
  };

  const jsonInit = (payload: unknown): { init: RequestInit } => ({
    init: {
      method: 'POST', // overridden by the RPC method; only `body`/`headers` matter here
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    },
  });

  return {
    mode: 'api',
    list: async () => {
      const res = await client.plans.$get();
      if (!res.ok) throw await apiError(res);
      const body = (await res.json()) as { plans: PlanSummary[] };
      return body.plans.map((p) => ({
        id: p.id,
        name: p.name,
        schemaVersion: p.schemaVersion,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      }));
    },
    get: async (id) => {
      const res = await client.plans[':id'].$get({ param: { id } });
      if (res.status === 404) return null;
      if (!res.ok) throw await apiError(res);
      return toRecordFromWire(await res.json());
    },
    create: async (write, id) => {
      const res = await client.plans.$post(
        {},
        jsonInit({ name: write.name, document: write.document, ...(id ? { id } : {}) }),
      );
      if (!res.ok) throw await apiError(res);
      return toRecordFromWire(await res.json());
    },
    update: async (id, write) => {
      const res = await client.plans[':id'].$put(
        { param: { id } },
        jsonInit({ name: write.name, document: write.document }),
      );
      if (res.status === 404) return null;
      if (!res.ok) throw await apiError(res);
      return toRecordFromWire(await res.json());
    },
    remove: async (id) => {
      const res = await client.plans[':id'].$delete({ param: { id } });
      if (res.status === 404) return false;
      if (!res.ok) throw await apiError(res);
      return true;
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Factory                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Build the active plan store: the RPC store when an API base URL is configured, else the
 * localStorage store. This is the ONE place the persistence backend is selected; the rest of the
 * app just consumes a `PlanStore`.
 */
export function createPlanStore(): PlanStore {
  if (isApiConfigured()) {
    const client = createApiClient();
    if (client !== null) return createApiPlanStore(client);
  }
  return createLocalPlanStore();
}
